import { IconArrowRight } from "@decibeltrade/react-icons";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { LinkButton } from "@/components/buttons/link-button";
import { Skeleton } from "@/components/skeleton";
import { useAffiliateAccrualsQuery } from "@/contexts/queries/affiliate-accruals";
import { useReferralFeesQuery } from "@/contexts/queries/referral-fees";
import { useRewardsSummary } from "@/contexts/queries/rewards/use-rewards-summary";
import { NumberFormatter } from "@/utils/number-formatter";

import { StatusTag } from "../../components";
import { formatKpiUsd } from "../../kpi-format";
import { affiliateClaimableUsd, toAccrualView } from "./commissions-accrual";
import { toCommissionsBasis } from "./commissions-basis";

/** Matches the volume window the rest of the overview reports on. */
const BASIS_WINDOW_DAYS = 30;

/**
 * Where the figure comes apart: the commissions page reports the same 30-day window, split
 * per client, under the same fee basis. It is the question the cell provokes — "who
 * generated it?" — so it is the one link the cell carries.
 */
const BREAKDOWN_HREF = "/affiliates/commissions";

/** Where commissions are actually claimed. The cell only points at it. */
const CLAIM_HREF = "/rewards";

/**
 * The hero's gold cell: what the affiliate has accrued, and separately what they can claim.
 *
 * With the accrual ledger reporting, the figure is what was actually EARNED this week and how
 * far that is from the payout floor. That is a commission amount, and it is publishable because
 * it comes from a stamped ledger row rather than a rate someone might later change: the row
 * records what was accrued under the rules in force that day.
 *
 * Without it — an environment where the ledger does not exist yet — it falls back to the fee
 * basis and publishes no commission amount and no rate. A figure shown from a rate that is not
 * signed off would be read as a promise. The basis is a fact either way, and the caption names
 * it as fee revenue over a window so it is never mistaken for a payout owed.
 *
 * Claimable USDC is a third thing, read from the on-chain campaign summary the Rewards page
 * uses. It is summed across every affiliate campaign and needs no ledger, so it renders in
 * either mode above.
 */
export const CommissionsCell = ({ account }: { account: string | undefined }) => {
  const t = useTranslations("affiliates.overview.hero");
  const feesQuery = useReferralFeesQuery({
    referrerAccount: account,
    days: BASIS_WINDOW_DAYS,
  });
  const accrualsQuery = useAffiliateAccrualsQuery({
    affiliateAccount: account,
    days: BASIS_WINDOW_DAYS,
  });
  // Shares React Query's cache with the Rewards page, so this costs no extra request there.
  const summary = useRewardsSummary(account);
  const basis = toCommissionsBasis(feesQuery.data);
  const accrual = toAccrualView(accrualsQuery.data);
  const claimableUsd = affiliateClaimableUsd(summary.data);

  // Commission mode is the side that settles in dollars — the same `mode === "usd"` check the
  // rewards-mode card reads. It gates both the payable figure below and the Active badge above.
  const isCommissionMode = accrualsQuery.data?.mode === "usd";

  // The accrued figure is a payout the affiliate is owed only when they are on Commission. In
  // Points mode the same accrual rows exist — the ledger records what the network would have paid
  // — but it settles as Amps, so showing it under "accrued this week · pays Wednesday" promises
  // dollars that never come; there the cell shows the fee revenue the network generated instead,
  // which reads as activity rather than a debt. Gated on mode alone: a genuine $0 week on
  // Commission still reads as an accrued $0, a real reportable zero, not a fee-revenue fallback.
  const payableAccrual = isCommissionMode ? accrual : null;

  // The accrual is the better answer whenever it exists, so its fetch gates the skeleton
  // too. Painting the basis first and swapping in the accrual a moment later would show two
  // different figures for the same cell.
  // `isLoading`, not `isPending`. A disabled query — the wallet has not hydrated, so there is no
  // way to sign the request yet — reports `isPending` forever, because React Query cannot tell
  // "never asked" from "asking". Driving the skeleton off it leaves the cell spinning on a request
  // that was never sent. `isLoading` is `isPending && isFetching`, true only while one is in
  // flight.
  const isPending = feesQuery.isPending || accrualsQuery.isLoading;

  // Three states, not two. An environment with no accrual ledger answers successfully with
  // nothing, and the fee basis is the honest thing to show there — that is what this cell did
  // before the ledger existed. A request that failed, or never ran for want of a session, is a
  // different claim: we do not know. Printing the basis there puts a real number about the
  // network's fee revenue under a COMMISSIONS heading and calls it the affiliate's, and nobody
  // reads the caption before reading a number that size.
  const accrualUnavailable = accrual === null && !accrualsQuery.isSuccess;

  return (
    <div className={cellStyles}>
      <div className={flex({ gap: "8", alignItems: "center" })}>
        <div className={sectionLabelStyles}>{t("commissions")}</div>
        {/* Only on Commission mode. On Points the figure below is the network's fee revenue, not a
            commission being paid, so an "Active" badge beside a dollar amount read as a debt owed —
            a Points affiliate would think they were due both Amps and a commission. Before the mode
            loads we assert neither. Same gate the rewards-mode card leads with, where the inactive
            mode carries no tag at all. */}
        {isCommissionMode ? <StatusTag variant="lime" label={t("commissionsActiveTag")} /> : null}
      </div>

      {/* The skeleton tracks the figure's own line box — the same clamp scaled by the 1.23
          line-height — so the figure lands where the skeleton stood instead of shifting the
          caption and the link down with it at whatever width the cell happens to be. */}
      {isPending ? (
        <Skeleton
          className={css({
            w: "[min(200px, 80cqw)]",
            h: "[clamp(2.15rem, 26cqw, 4rem)]",
            rounded: "200",
          })}
        />
      ) : (
        <div className={amountStyles}>
          {accrualUnavailable
            ? "—"
            : formatKpiUsd(payableAccrual ? payableAccrual.weekUsd : basis.basisUsd)}
        </div>
      )}

      {/* Short enough to hold one line at the cell's narrowest. The long form —
          "Fee revenue your network generated · last 30 days" — wrapped into two ragged lines
          directly under the figure. Both halves still have to survive the shortening: the
          basis, so the amount is never read as a payout owed, and the window, because 30 days
          and all-time are very different numbers. */}
      {payableAccrual ? (
        <div className={stack({ gap: "4" })}>
          <div className={heroSubStyles}>{t("accruedCaption")}</div>
          {/* The floor is the cell's real news once an affiliate is accruing: a week under
              it is forfeited outright, not carried, so "$0.40 to go" is the difference
              between being paid and not. */}
          <div className={heroSubStyles}>
            {payableAccrual.meetsMinimum
              ? t("accruedMeetsMinimum", {
                  minimum: NumberFormatter.usdc(payableAccrual.minimumUsd),
                })
              : t("accruedMinimumHint", {
                  amount: NumberFormatter.usdc(payableAccrual.shortfallUsd),
                  minimum: NumberFormatter.usdc(payableAccrual.minimumUsd),
                })}
          </div>
          {/* Last week as history: a week closes Sunday and for the first days of the next one
              the figure above reads near zero while this is the number that matters. Whether it
              can be claimed yet is the claim row's job, so this line names no payday. */}
          {payableAccrual.previous ? (
            <div className={previousWeekStyles}>
              {payableAccrual.previous.swept
                ? t("previousWeekPending", {
                    amount: NumberFormatter.usdc(payableAccrual.previous.usd),
                  })
                : t("previousWeekUnswept", {
                    amount: NumberFormatter.usdc(payableAccrual.previous.usd),
                  })}
            </div>
          ) : null}
        </div>
      ) : accrualUnavailable ? (
        <div className={heroSubStyles}>{t("accrualUnavailable")}</div>
      ) : (
        <div className={heroSubStyles}>{t("basisCaption", { days: BASIS_WINDOW_DAYS })}</div>
      )}

      {/* What is claimable now, summed over every affiliate campaign. One claim surface: Rewards
          already owns pending, rejected, expired and claimed. */}
      {claimableUsd > 0 ? (
        <div className={claimRowStyles}>
          <span className={previousWeekStyles}>
            {t("claimableAmount", { amount: NumberFormatter.usdc(claimableUsd) })}
          </span>
          <LinkButton variant="primary" size="sm" href={CLAIM_HREF} TailIcon={IconArrowRight}>
            {t("claim")}
          </LinkButton>
        </div>
      ) : null}

      {/* Anchored to the floor, like the rate row and the usage line in the two cells beside
          it — those set a line along the bottom of the banner that this cell was the only one
          not meeting. A hairline rather than the siblings' filled row: a `background.secondary`
          block sits badly on the amber gradient. */}
      <Link href={BREAKDOWN_HREF} className={breakdownLinkStyles}>
        {t("basisLinkLabel")}
        <IconArrowRight className={breakdownArrowStyles} />
      </Link>
    </div>
  );
};

/**
 * Top-left, like the two cells beside it in the banner: their section labels sit on the top
 * edge, so centring this one put "COMMISSIONS" halfway down the row and broke the line the
 * three labels are read along. Centred looked deliberate on its own and wrong in the row.
 *
 * `containerType` makes this box the reference for the figure's `cqw` size below. It has to
 * be the inner stack rather than the banner cell: `cqw` then resolves against the content
 * width, with the cell's padding already taken off.
 */
const cellStyles = stack({ gap: "12", h: "full", containerType: "inline-size" });

const sectionLabelStyles = css({
  textStyle: "highlight.sm",
  letterSpacing: "[1px]",
  textTransform: "uppercase",
  color: "text.tertiary",
});

const heroSubStyles = css({
  textStyle: "body.md",
  color: "text.tertiary",
});

/**
 * A step brighter than the captions above it. This line is the only place the cell reports
 * money that is owed but not on screen anywhere else, so it has to survive being skimmed —
 * at `text.tertiary` it read as a third line of small print and was missed.
 */
const previousWeekStyles = css({
  textStyle: "highlight.sm",
  color: "text.secondary",
});

const claimRowStyles = flex({
  gap: "12",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
});

/**
 * The figure leads the cell at `display.lg` (52px) where there is room, at medium weight
 * instead of the display default of light, which at this size reads as thin.
 *
 * The size is fluid because a fixed one cannot hold. Measured in Chrome at the real font:
 * "$1.52K" paints 180px of ink at 52px, and the gold cell's content box is 141px at a 900px
 * window — the figure ran straight over the divider into the cell next door. An eight-glyph
 * value ("$999.99M") paints 240px, which overflowed even the widest cell this banner ever
 * gives it.
 *
 * 21cqw is that ink ratio inverted: the widest value `formatKpiUsd` can return is ~4.6x its
 * font size, so 1/4.6 of the container is the largest size that always fits. The clamp caps
 * it at 52px once the cell is wide enough to stop being the constraint, and floors it at 28px
 * so a very narrow cell shrinks the figure rather than the other way round.
 */
const amountStyles = css({
  textStyle: "display.lg",
  fontSize: "[clamp(1.75rem, 21cqw, 3.25rem)]",
  fontWeight: "medium",
  color: "text.primary",
});

const breakdownLinkStyles = flex({
  mt: "auto",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8",
  pt: "12",
  borderTopWidth: "100",
  borderTopStyle: "solid",
  borderTopColor: "border.primary",
  textStyle: "highlight.sm",
  color: "text.secondary",
  transitionPreset: "colors",
  /** The arrow is nudged from the link, not from itself, so hovering the label moves it too. */
  _hover: { color: "text.primary", "& svg": { transform: "translateX(2px)" } },
});

const breakdownArrowStyles = css({
  w: "16",
  h: "16",
  flexShrink: "0",
  transitionPreset: "transform",
});
